import React, { useState, useEffect, useRef } from 'react';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  placeholder?: string;
  required?: boolean;
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  placeholder = "ساعت را انتخاب کنید",
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number>(14);
  const [selectedMinute, setSelectedMinute] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hourListRef = useRef<HTMLDivElement>(null);
  const minuteListRef = useRef<HTMLDivElement>(null);

  // تبدیل value به ساعت و دقیقه
  useEffect(() => {
    if (value) {
      const parts = value.split(':');
      if (parts.length === 2) {
        const hour = parseInt(parts[0]);
        const minute = parseInt(parts[1]);
        if (!isNaN(hour) && hour >= 0 && hour <= 23) {
          setSelectedHour(hour);
        }
        if (!isNaN(minute) && minute >= 0 && minute <= 59) {
          setSelectedMinute(minute);
        }
      }
    } else {
      // مقدار پیش‌فرض: 14:00
      setSelectedHour(14);
      setSelectedMinute(0);
    }
  }, [value]);

  // بستن picker با کلیک خارج
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // اسکرول به ساعت و دقیقه انتخاب شده
      setTimeout(() => {
        if (hourListRef.current) {
          const hourElement = hourListRef.current.children[selectedHour] as HTMLElement;
          if (hourElement) {
            hourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        if (minuteListRef.current) {
          const minuteElement = minuteListRef.current.children[selectedMinute] as HTMLElement;
          if (minuteElement) {
            minuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, selectedHour, selectedMinute]);

  const handleTimeSelect = () => {
    const formattedTime = `${selectedHour.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}`;
    onChange(formattedTime);
    setIsOpen(false);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const displayValue = value || '';

  return (
    <div className="relative" ref={wrapperRef} style={{ direction: 'rtl' }}>
      <input
        type="text"
        value={displayValue}
        readOnly
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeholder}
        required={required}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
      />
      
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4" style={{ direction: 'rtl', minWidth: '280px' }}>
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">انتخاب ساعت</h3>
          </div>

          <div className="flex items-center justify-center gap-4 mb-4">
            {/* انتخاب ساعت */}
            <div className="flex flex-col items-center">
              <div 
                ref={hourListRef}
                className="border border-gray-300 rounded-lg overflow-hidden max-h-48 overflow-y-auto scroll-smooth" 
                style={{ width: '80px' }}
              >
                {hours.map((hour) => (
                  <button
                    key={hour}
                    onClick={() => setSelectedHour(hour)}
                    className={`w-full px-4 py-2 text-sm transition-colors ${
                      selectedHour === hour
                        ? 'bg-blue-600 text-white font-semibold'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {hour.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>

            <div className="text-2xl font-bold text-gray-400">:</div>

            {/* انتخاب دقیقه */}
            <div className="flex flex-col items-center">
              <div 
                ref={minuteListRef}
                className="border border-gray-300 rounded-lg overflow-hidden max-h-48 overflow-y-auto scroll-smooth" 
                style={{ width: '80px' }}
              >
                {minutes.map((minute) => (
                  <button
                    key={minute}
                    onClick={() => setSelectedMinute(minute)}
                    className={`w-full px-4 py-2 text-sm transition-colors ${
                      selectedMinute === minute
                        ? 'bg-blue-600 text-white font-semibold'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {minute.toString().padStart(2, '0')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* نمایش زمان انتخاب شده */}
          <div className="text-center mb-4">
            <div className="inline-block px-6 py-3 bg-blue-50 rounded-lg">
              <span className="text-2xl font-bold text-blue-600">
                {selectedHour.toString().padStart(2, '0')}:{selectedMinute.toString().padStart(2, '0')}
              </span>
            </div>
          </div>

          {/* دکمه‌های عملیات */}
          <div className="flex gap-2">
            <button
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              لغو
            </button>
            <button
              onClick={handleTimeSelect}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              تایید
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;

